import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import blogPosts from './blogPosts';

function PostContent({ post }) {
  return (
    <motion.article
      key={post.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <p className="inline-flex rounded-lg border border-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary">
        {post.category}
      </p>
      <h1 className="mt-5 text-balance text-3xl font-semibold tracking-tight text-gray-950 sm:text-4xl">
        {post.title}
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-500">
        <span>Afroawi Insights</span>
        <span aria-hidden="true">•</span>
        <span>{post.readTime}</span>
      </div>

      <div className="prose prose-slate mt-8 max-w-none">
        {post.sections.map((section, index) => {
          if (section.type === 'heading') {
            return (
              <h2 key={index} className="mt-10 text-2xl font-semibold tracking-tight text-gray-950">
                {section.text}
              </h2>
            );
          }

          if (section.type === 'list') {
            const ListTag = section.ordered ? 'ol' : 'ul';
            return (
              <ListTag key={index} className="mt-4 space-y-3">
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex gap-3 rounded-lg border border-gray-100 bg-slate-50 p-4 text-sm leading-6 text-gray-700">
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-secondary-light/30 text-xs font-semibold text-primary">
                      {section.ordered ? itemIndex + 1 : '•'}
                    </span>
                    {item}
                  </li>
                ))}
              </ListTag>
            );
          }

          return (
            <p key={index} className="mt-4 text-base leading-7 text-gray-700">
              {section.text}
            </p>
          );
        })}
      </div>
    </motion.article>
  );
}

export default function BlogScreen() {
  const [selectedId, setSelectedId] = useState(blogPosts[0].id);
  const selectedPost = blogPosts.find((post) => post.id === selectedId) || blogPosts[0];

  return (
    <main className="bg-white pt-28 sm:pt-32">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <span className="h-2 w-2 flex-none rounded-sm bg-secondary" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-dark">Blog & news</p>
          </div>
          <h1 className="text-balance text-4xl font-semibold tracking-tight text-gray-950 sm:text-5xl">
            Ideas on AI, design, and building better software.
          </h1>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-[20rem_1fr] lg:items-start">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <ul className="space-y-2">
              {blogPosts.map((post) => {
                const isActive = post.id === selectedId;

                return (
                  <li key={post.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(post.id)}
                      aria-current={isActive}
                      className={`block w-full rounded-lg border p-4 text-left transition duration-200 ${
                        isActive
                          ? 'border-secondary bg-secondary/10'
                          : 'border-gray-200 hover:border-secondary/60'
                      }`}
                    >
                      <p className={`text-xs font-medium uppercase tracking-wide ${isActive ? 'text-secondary-dark' : 'text-primary'}`}>
                        {post.category}
                      </p>
                      <p className="mt-2 text-sm font-semibold leading-6 text-gray-950">{post.title}</p>
                      <p className="mt-2 text-xs text-gray-500">{post.readTime}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <div className="rounded-lg border border-gray-200 p-6 sm:p-10">
            <AnimatePresence mode="wait">
              <PostContent post={selectedPost} />
            </AnimatePresence>
          </div>
        </div>
      </div>
    </main>
  );
}
